
import {
  computed, defineComponent, onMounted, reactive, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import useVuelidate from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import StepShell from '@/components/onboarding-v2/shared/StepShell.vue';
import CountryDropdown from '@/components/fields/CountryDropdown.vue';
import { useOnboardingProfile } from '@/composables/onboarding-v2/useOnboardingProfile';
import { showToastError } from '@/helpers';

export default defineComponent({
  name: 'UserInfosStep',

  components: {
    StepShell,
    InputText,
    CountryDropdown,
  },

  props: {
    canGoBack: { type: Boolean, default: false },
  },

  emits: ['next', 'back'],

  setup(_, { emit }) {
    const { t } = useI18n();
    const {
      profile, profileRaw, preload, saveProfile,
    } = useOnboardingProfile();

    const form = reactive({
      firstName: '',
      lastName: '',
      phoneMobile: '',
      societe: '',
      vatNumber: '',
      rue: '',
      zipCode: '',
      city: '',
      country: '',
    });

    const countryWrapper = computed({
      get: () => ({ country: form.country, state: '' }),
      set: (value: { country: string; state: string }) => {
        form.country = value.country;
      },
    });

    const countryConfig = {
      showLabels: false,
      showStates: false,
      showFlags: true,
      showPhonesCodes: false,
      showOnlyPhoneCodeForSelection: false,
    };

    const countryStyles = {
      component: 'width:100%',
      country: 'width:100%; align-items: center; height: 2.3rem;',
      state: 'width:100%',
    };

    const hydrated = ref(false);
    let initialSnapshot = '';

    const snapshot = () => JSON.stringify({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phoneMobile: form.phoneMobile.trim(),
      societe: form.societe.trim(),
      vatNumber: form.vatNumber.trim(),
      rue: form.rue.trim(),
      zipCode: form.zipCode.trim(),
      city: form.city.trim(),
      country: form.country,
    });

    const hydrate = () => {
      form.firstName = profile.firstName;
      form.lastName = profile.lastName;
      form.phoneMobile = profile.phoneMobile;
      form.societe = profile.societe;
      form.vatNumber = profile.vatNumber;
      form.rue = profile.rue;
      form.zipCode = profile.zipCode;
      form.city = profile.city;
      form.country = profile.country;
      initialSnapshot = snapshot();
    };

    onMounted(async () => {
      await preload();
      hydrate();
      hydrated.value = true;
    });

    const rules = computed(() => ({
      firstName: { required, minLength: minLength(2) },
      lastName: { required, minLength: minLength(2) },
      phoneMobile: { required },
      societe: { required, minLength: minLength(1) },
      rue: { required, minLength: minLength(2) },
      zipCode: { required },
      city: { required, minLength: minLength(2) },
      country: { required },
    }));

    const v$ = useVuelidate(rules, form);

    const saving = ref(false);

    const handleNext = async () => {
      const ok = await v$.value.$validate();

      if (!ok) return;
      if (snapshot() === initialSnapshot) {
        emit('next');
        return;
      }
      saving.value = true;
      try {
        profileRaw.firstName = form.firstName.trim();
        profileRaw.lastName = form.lastName.trim();
        profileRaw.phoneMobile = form.phoneMobile.trim();
        profileRaw.societe = form.societe.trim();
        profileRaw.vatNumber = form.vatNumber.trim();
        profileRaw.rue = form.rue.trim();
        profileRaw.zipCode = form.zipCode.trim();
        profileRaw.city = form.city.trim();
        profileRaw.country = form.country;
        await saveProfile();
        initialSnapshot = snapshot();
        emit('next');
      } catch (err) {
        await showToastError('GENERIC_ERROR');
      } finally {
        saving.value = false;
      }
    };

    return {
      t,
      form,
      v$,
      saving,
      hydrated,
      countryWrapper,
      countryConfig,
      countryStyles,
      handleNext,
    };
  },
});
